import Modal from "components/modal";
import ModalTemplate from "components/modal/modal-template";
import ToggleAudio from "components/toggleAudio";
import Typography from "components/typography";
import React, { useRef, useState } from "react";
import styled from "styled-components";
import { Colors } from "utils/colors";
import { FormatAudioDuration } from "utils/dateConvert";
import { ITrack } from "types/musicData";

interface IProps {
  editTrack: () => void;
  track: ITrack;
  number: number;
}
const TrackItem = ({ editTrack, track, number }: IProps) => {
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const handleLoadedMetadata = (event: any) => {
    const audioDuration = event.target.duration;
    setDuration(audioDuration);
  };

  const handleTimeUpdate = (event: any) => {
    const current = event.target.currentTime;
    setCurrentTime(current);
  };

  const TogglePlay = () => {
    if (audioRef.current && !isPlaying) {
      audioRef.current.play();
      setIsPlaying(true);
    }
    if (audioRef.current && isPlaying) {
      setIsPlaying(false);
      audioRef.current.pause();
    }
  };
  return (
    <Container>
      <audio
        controls
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={handleTimeUpdate}
        style={{ display: "none" }}
        ref={audioRef}
      >
        <source src={URL.createObjectURL(track.file)} type="audio/mpeg" />
        Your browser does not support the audio element.
      </audio>
      <TrackDetails>
        <ReorderIcon>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="100%"
            height="100%"
            viewBox="0 0 24 25"
            fill="none"
          >
            <path
              d="M19 10.5H5"
              stroke="#CDCDCD"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <path
              d="M19 14.5H5"
              stroke="#CDCDCD"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <path
              d="M19 6.5H5"
              stroke="#CDCDCD"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <path
              d="M19 18.5H5"
              stroke="#CDCDCD"
              stroke-width="1.5"
              stroke-linecap="round"
            />
          </svg>
        </ReorderIcon>
        <Typography mr="1.2" size="s" color={Colors.white100}>
          {number}
        </Typography>
        <PlayTimeContainer>
          <ToggleAudio toggleAudio={() => TogglePlay()} />
          <Typography size="xs" mt="0.4" color={Colors.grey500}>
            {FormatAudioDuration(duration)}
          </Typography>
        </PlayTimeContainer>
        <TrackText>
          <Typography size="s" weight="medium" mb="0.4">
            {track.trackDetails?.title}
          </Typography>
          <Typography size="xs" weight="medium" color={Colors.grey500}>
            {track.trackArtists?.length ? track.trackArtists[0].name : ""}
            {/* •
            Vibe King */}
          </Typography>
        </TrackText>
      </TrackDetails>
      <TrackOption onClick={editTrack}>
        <Typography mr="0.4" color={Colors.yellow400} m_display="none">
          Edit
        </Typography>
        <OptionIcon>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="100%"
            height="100%"
            viewBox="0 0 14 15"
            fill="none"
          >
            <path
              d="M8.37803 2.87906L8.91866 2.33838C9.81448 1.44255 11.2669 1.44255 12.1628 2.33838C13.0586 3.2342 13.0586 4.68663 12.1628 5.58246L11.6221 6.12313M8.37803 2.87906C8.37803 2.87906 8.44558 4.028 9.45935 5.04178C10.4731 6.05555 11.6221 6.12313 11.6221 6.12313M8.37803 2.87906L3.40728 7.84978C3.0706 8.18648 2.90226 8.35477 2.75749 8.54039C2.58671 8.75937 2.4403 8.99626 2.32083 9.24692C2.21956 9.45943 2.14428 9.6853 1.99371 10.137L1.35569 12.0511M11.6221 6.12313L6.65136 11.0939C6.31466 11.4305 6.14637 11.5989 5.96075 11.7436C5.74178 11.9144 5.50487 12.0609 5.25421 12.1803C5.04171 12.2816 4.81585 12.3568 4.36416 12.5074L2.45009 13.1455M1.35569 12.0511L1.19973 12.519C1.12564 12.7412 1.18349 12.9863 1.34917 13.152C1.51485 13.3177 1.75992 13.3755 1.98221 13.3014L2.45009 13.1455M1.35569 12.0511L2.45009 13.1455"
              stroke="#FEB73D"
              stroke-width="1.02083"
            />
          </svg>
        </OptionIcon>
      </TrackOption>
      <CheckMark>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="100%"
          height="100%"
          viewBox="0 0 24 25"
          fill="none"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M22 12.5C22 18.0228 17.5228 22.5 12 22.5C6.47715 22.5 2 18.0228 2 12.5C2 6.97715 6.47715 2.5 12 2.5C17.5228 2.5 22 6.97715 22 12.5ZM16.0303 9.46967C16.3232 9.76256 16.3232 10.2374 16.0303 10.5303L11.0303 15.5303C10.7374 15.8232 10.2626 15.8232 9.96967 15.5303L7.96967 13.5303C7.67678 13.2374 7.67678 12.7626 7.96967 12.4697C8.26256 12.1768 8.73744 12.1768 9.03033 12.4697L10.5 13.9393L12.7348 11.7045L14.9697 9.46967C15.2626 9.17678 15.7374 9.17678 16.0303 9.46967Z"
            fill="#5AB400"
          />
        </svg>
      </CheckMark>
    </Container>
  );
};

export default TrackItem;

const Container = styled.div`
  background-color: ${Colors.grey600};
  padding: 2rem 1.2rem;
  border-radius: 8px;
  margin-bottom: 2.4rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
`;

const TrackDetails = styled.div`
  display: flex;
  align-items: center;
  width: 50%;
`;

const ReorderIcon = styled.div`
  width: 2.4rem;
  height: 2.4rem;
  margin-right: 1.2rem;
`;
const PlayTimeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-right: 2.4rem;
`;

const TrackText = styled.div``;

const TrackOption = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const OptionIcon = styled.div`
  width: 1.4rem;
  height: 1.4rem;
  @media only screen and (max-width: 769px) {
    width: 2rem;
    height: 2rem;
  }
`;

const CheckMark = styled.div`
  width: 2.4rem;
  height: 2.4rem;
  margin-left: 2.4rem;
`;
